'use client';
import React, { useEffect, useState } from 'react';
import { Flame, CalendarCheck, TrendingDown, Award } from 'lucide-react';
import { getUserProfile } from '@/lib/services/betprotectService';
import { useAuth } from '@/contexts/AuthContext';
import MilestoneBadges from './MilestoneBadges';
import { computeMilestoneStats, type MilestoneStats } from '@/lib/milestones';
import { loadProfile } from '@/lib/userLearning';
import Icon from '@/components/ui/AppIcon';



interface StatItem {
  key: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  gradientFrom: string;
  gradientTo: string;
  value: string;
  label: string;
  sub: string;
  trend: string | null;
}

const defaultStats: StatItem[] = [
  {
    key: 'stat-streak',
    icon: Flame,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-500',
    gradientFrom: 'rgba(249,115,22,0.08)',
    gradientTo: 'rgba(249,115,22,0.02)',
    value: '—',
    label: 'Day streak',
    sub: 'Keep it going!',
    trend: null,
  },
  {
    key: 'stat-urge-free',
    icon: CalendarCheck,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    gradientFrom: 'rgba(34,197,94,0.08)',
    gradientTo: 'rgba(34,197,94,0.02)',
    value: '—',
    label: 'Urge-free days',
    sub: 'This month',
    trend: null,
  },
  {
    key: 'stat-avg-urge',
    icon: TrendingDown,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
    gradientFrom: 'rgba(0,51,153,0.08)',
    gradientTo: 'rgba(0,51,153,0.02)',
    value: '—',
    label: 'Avg urge score',
    sub: 'Last 7 days',
    trend: null,
  },
  {
    key: 'stat-exercises',
    icon: Award,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    gradientFrom: 'rgba(147,51,234,0.08)',
    gradientTo: 'rgba(147,51,234,0.02)',
    value: '—',
    label: 'Exercises done',
    sub: 'Total',
    trend: null,
  },
];

export default function StreakStats() {
  const { user } = useAuth();
  const [stats, setStats] = useState<StatItem[]>(defaultStats);
  const [loading, setLoading] = useState(true);
  const [milestoneStats, setMilestoneStats] = useState<MilestoneStats | null>(null);

  useEffect(() => {
    if (!user) {
      // Load from local profile for unauthenticated users
      const profile = loadProfile();
      setMilestoneStats(computeMilestoneStats({
        streakDays: profile.streakDays,
        exercisesDone: profile.totalExercisesDone,
        totalCheckIns: profile.totalCheckIns,
      }));
      setLoading(false);
      return;
    }
    getUserProfile()
      .then((profile) => {
        if (profile) {
          setStats([
            { ...defaultStats[0], value: String(profile.streakDays) },
            { ...defaultStats[1], value: String(profile.urgeFreedays) },
            { ...defaultStats[2], value: '3.1', trend: '↓ improving' },
            { ...defaultStats[3], value: String(profile.exercisesDone) },
          ]);
          setMilestoneStats(computeMilestoneStats({
            streakDays: profile.streakDays,
            exercisesDone: profile.exercisesDone,
            totalCheckIns: 0,
          }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.key}
              className="relative bg-card rounded-xl border border-border px-5 py-4 flex items-start gap-3 hover:shadow-md transition-all duration-200 overflow-hidden group"
            >
              {/* Gradient background accent */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                style={{ background: `linear-gradient(135deg, ${s.gradientFrom}, ${s.gradientTo})` }}
              />
              {/* Top-right decorative circle */}
              <div
                className="absolute -top-4 -right-4 w-16 h-16 rounded-full opacity-5 pointer-events-none"
                style={{ background: s.iconColor.replace('text-', '') }}
              />
              <div className={`relative w-9 h-9 rounded-lg ${s.iconBg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <Icon size={18} className={s.iconColor} />
              </div>
              <div className="relative min-w-0">
                {loading ? (
                  <div className="h-7 w-12 bg-muted rounded animate-pulse mb-1" />
                ) : (
                  <p className="text-2xl font-800 text-foreground font-tabular leading-none">{s.value}</p>
                )}
                <p className="text-xs font-600 text-muted-foreground mt-1">{s.label}</p>
                <p className="text-xs text-muted-foreground">{s.sub}</p>
                {s.trend && <p className="text-xs font-600 text-green-600 mt-1">{s.trend}</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Compact milestone badges */}
      {milestoneStats && (
        <div className="bg-card rounded-xl border border-border px-4 py-3 flex items-center gap-3 flex-wrap">
          <span className="text-xs font-600 text-muted-foreground flex-shrink-0">Milestones:</span>
          <MilestoneBadges stats={milestoneStats} compact />
          <span className="text-xs text-muted-foreground ml-auto hidden sm:block">
            Visit your profile to see full badge details
          </span>
        </div>
      )}
    </div>
  );
}